import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Chip, Button, Tooltip } from '@arnsbogroup/react-voicearchive'
import Waveform from 'components/Waveform'
import classNames from 'classnames'
import Icon from 'components/Icon'
import genderNames from 'data/genders/messages'
import languageNames from 'data/languages/messages'
import {
  FormattedMessage,
  FormattedRelativeTime,
  FormattedList,
  FormattedDateTimeRange,
  FormattedDate,
  FormattedNumber,
  useIntl,
} from 'react-intl'
import messages from './messages'
import DemoPlayButton from 'containers/DemoPlayButton'
import useColor from 'hooks/useColor'
import useCurrentUser from 'hooks/useCurrentUser'
import { selectUnit } from '@formatjs/intl-utils'
import EditableImg from 'components/EditableImg'
import { saveAs } from 'file-saver'
import { sortDemos } from 'contexts/ProjectContext'
import CharacterSelect from './components/CharacterSelect'

const THREE_MONTHS_AGO = Date.now() - 2629800000 * 3

const Voice = ({
  product,
  dispatch,
  focused = false,
  editable = false,
  max = undefined,
  onSelect,
  onToggle,
  readOnly = false,
  selected = false,
  expandable = false,
  downloadable = false,
  navigate,
  voice,
  vote,
  onVote,
  onRedoVote,
  onDownload = undefined,
  projectVotes = [],
  characterMap = {}
}) => {
  const [showAll, setShowAll] = useState(false)
  const { roles = [] } = useCurrentUser()
  const color = useColor(voice.sku)
  const { formatMessage } = useIntl()

  const _onDownload = onDownload || ((demo) => {
    saveAs(demo.url, `${voice.sku} - ${demo.productSlug} (${demo.id})`)
  })

  const demos = useMemo(() => {
    let demos = sortDemos(voice.demos || [], product)

    if (!demos || demos.length == 0) {
      return []
    }

    if (product && !showAll) {
      const productDemos = demos
        .filter((demo) => demo.productSlug == product)

      if (productDemos.length > 0) {
        return productDemos.slice(0, showAll ? productDemos.length : max)
      } else {
        return showAll ? demos : [demos[0]]
      }
    }

    if (demos.length > 0) {
      return demos.slice(0, showAll ? (voice.demos || []).length : max)
    }

    return voice._demos || []
  }, [product, voice.demos, showAll, max])

  const isNew = useMemo(() => {
    const pastTime = new Date(voice.created)
    const now = new Date()

    const thirtyDaysInMs = 40 * 24 * 60 * 60 * 1000

    const timeDiffInMs = now.getTime() - pastTime.getTime()

    return timeDiffInMs < thirtyDaysInMs
  }, [voice.created])

  const shortTurnaroundTime = useMemo(() => {
    if (voice.turnaround_time && voice.turnaround_time.split(' ').length > 1) {
      const turnaroundTimeArr = voice.turnaround_time.split(' ')
      if (Number.isInteger(turnaroundTimeArr[0]) || parseInt(turnaroundTimeArr[0])) {
        return turnaroundTimeArr[0] + turnaroundTimeArr[1].substring(0,1)
      }
      return turnaroundTimeArr[0].substring(0,1) + turnaroundTimeArr[1].substring(0,1)
    }
    return voice.turnaround_time
  }, [voice.turnaround_time])

  const currentUnavailability = useMemo(() => {
    const candidates = (voice.unavailability || [])
      .filter(({ current }) => current)
      .sort((a, b) => a.from.localeCompare(b.from))

    return candidates?.[0] || null
  }, [voice?.unavailability])

  return (
    <Card
      onClick={onSelect}
      key={voice.id}
      style={{
        borderLeftColor: color,
        borderLeftWidth: '3px',
      }}
      className={classNames(
        'flex',
        'flex-col',
        'overflow-hidden',
        'break-inside-avoid',
        'hover:shadow-md',
        'transition-all',
        'group',
        'focus:border-blue-400',
        {
          'border-2': focused || selected,
          'border-blue-400': focused || selected,
          'bg-yellow-100': !!voice.note || !!voice.body,
          'cursor-pointer': !!onSelect,
        }
      )}
      tabIndex={0}
    >
      <div className={classNames('p-4 bg-white flex flex-grow', {
        'space-x-4': editable,
      })}>
        <EditableImg
          disabled={!editable}
          onChange={(avatarUrl) =>
            dispatch({ type: 'PATCH_VOICE', payload: { ...voice, avatarUrl } })
          }
        >
          {voice?.avatarUrl ? (
            <img src={voice.avatarUrl} className="w-16 h-16 rounded" />
          ) : editable ? (
            <div className="w-16 h-16 block cursor-pointer bg-gray-50" />
          ) : null}
        </EditableImg>
        <div className="w-full">
          <div className="w-full flex justify-between">
            <div>
              <div className="flex flex-wrap gap-2 justify-start text-gray-600">
                {voice?.status == 'ACTIVE' && (
                  <small className="rounded-sm p-1 text-xs font-medium bg-negative border border-negative text-white cursor-not-allowed">
                    <FormattedMessage {...messages.activeWarning} />
                  </small>
                )}

                {roles.includes('coordinator') &&
                  voice.ratings &&
                  !!Object.keys(voice.ratings).length && (
                    <span
                      className={classNames([
                        'rounded-sm p-1 text-xs font-medium',
                        {
                          'bg-positive text-white':
                            voice.featured && voice.ratings?.average > 6,
                          'border border-gray-300':
                            !voice.featured || voice.ratings?.average <= 6,
                        },
                      ])}
                    >
                      {voice.ratings?.average?.toFixed(2)}

                      {voice.ratings?.average > 8 && (
                        <Icon
                          icon={['far', 'arrow-up']}
                          className="ml-1text-positive text-xs"
                        />
                      )}
                    </span>
                  )}

                {isNew &&
                  <h2 className="block my-auto font-medium text-sm text-negative">
                    New <Icon icon={['fas', 'fire']} />
                  </h2>
                }

                {!!voice.age && voice.age < 18 &&
                  <small className="rounded-sm p-1 text-xs font-medium bg-secondary-lighter border border-secondary-lighter text-white">
                    Child voice <Icon icon={['fas', 'child']} />
                  </small>
                }

                { voice.featured ? (
                  <Tooltip
                    className="p-4 text-sm bg-gray-600 text-white"
                    tooltip={
                      <div className="max-w-sm space-y-4">
                        <h1 className="font-medium">Recommended <Icon icon={['fas', 'star']} /></h1>
                        <p><FormattedMessage {...messages.tooltip} /></p>
                      </div>
                    }
                  >
                    <h2 className={classNames('cursor-help my-auto block font-medium text-sm', { 'text-tertiary': !isNew, 'text-gray-400': isNew })}>
                      Recommended <Icon icon={['fas', 'star']} />
                    </h2>
                  </Tooltip>
                ) : voice.latestJobTimestamp * 1000 > THREE_MONTHS_AGO ? (
                  <div className="font-medium text-sm text-positive">
                    <FormattedMessage
                      {...messages.latestJob}
                      values={{
                        time: (
                          <FormattedRelativeTime
                            numeric="auto"
                            {...selectUnit(voice.latestJobTimestamp * 1000)}
                          />
                        ),
                      }}
                      tagName="span"
                    />
                    <div className="ml-1 w-2 h-2 rounded-full inline-block bg-positive" />
                  </div>
                ) : null}
              </div>
              <Link to={navigate || '#'} className="flex items-center">
                <h1 className="font-medium">
                  {voice.sku}
                </h1>
              </Link>
            </div>

            { (!onVote && !onRedoVote) && (
              <div className="flex flex-col 2xl:flex-row items-center space-x-2">
                <div className="flex space-x-3 text-xl">
                { projectVotes?.up?.hasOwnProperty(voice.id) && (
                  <span className="text-sm text-positive">
                    <Icon icon={['fas', 'thumbs-up']} />
                    <span className="ml-2">{ projectVotes.up[voice.id] }</span>
                  </span>
                )}
                { projectVotes?.down?.hasOwnProperty(voice.id) && (
                  <span className="text-sm text-negative">
                    <Icon icon={['fas', 'thumbs-down']} />
                    <span className="ml-2">{ projectVotes.down[voice.id] }</span>
                  </span>
                )}
                </div>
              </div>
            )}

            { ((voice.rate_commercial && voice.rate_video_presentation) || !readOnly || onVote || onRedoVote) &&
              <div className="flex flex-col 2xl:flex-row items-center space-x-2">
                { onVote ? (
                  <div className="flex space-x-3 text-xl">
                    <Button
                      variant="inline"
                      state="positive"
                      icon={['fas', 'thumbs-up']}
                      className="block opacity-75 hover:opacity-100"
                      onClick={() => onVote(1)}
                    />
                    <Button
                      variant="inline"
                      state="negative"
                      icon={['fas', 'thumbs-down']}
                      className="block opacity-75 hover:opacity-100"
                      onClick={() => onVote(-1)}
                    />
                  </div>
                ) : typeof vote !== 'undefined' ? (
                  <span className="group text-xl">
                    <span className="block group-hover:hidden text-gray-300">
                      <Icon icon={['fas', vote == 1 ? 'thumbs-up': 'thumbs-down']} />
                    </span>
                    <span className="hidden group-hover:block text-gray-600 group-hover:text-primary cursor-pointer">
                      <Icon icon="redo" onClick={onRedoVote} />
                    </span>
                  </span>
                ) : null }

                { voice.rate_commercial && voice.rate_video_presentation &&
                  <span className="text-sm text-gray-600 font-medium space-x-1 order-last 2xl:order-first">
                    <span title={formatMessage(messages.commercialBaseRate)}>
                      <FormattedNumber
                        value={parseInt(voice.rate_commercial)}
                        style="currency"
                        currency={voice?.rate_commercial_currency_iso ? voice?.rate_commercial_currency_iso : "EUR"}
                      />
                    </span>
                    <span>/</span>
                    <span title={formatMessage(messages.videoBaseRate)}>
                      <FormattedNumber
                        value={parseInt(voice.rate_video_presentation)}
                        style="currency"
                        currency={voice?.rate_video_presentation_currency_iso ? voice?.rate_video_presentation_currency_iso : "EUR"}
                      />
                    </span>
                  </span>
                }

                { !readOnly &&
                  <>
                    {selected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          dispatch({ type: 'REMOVE_VOICE', payload: voice })
                        }
                      >
                        <Icon icon="times" />{' '}
                        <FormattedMessage {...messages.deselectDemo} />
                      </Button>
                    ) : (
                      <>
                        {Object.entries(characterMap).length === 0 ? (
                          <Button
                            size="sm"
                            onClick={() =>
                              dispatch({ type: 'ADD_VOICE', payload: { voice } })
                            }
                          >
                            <Icon icon="plus" />{' '}
                            <FormattedMessage {...messages.selectDemo} />
                          </Button>
                        ) : (
                          <CharacterSelect
                            value={voice?.character}
                            onChange={(value) =>
                              dispatch({ type: 'ADD_VOICE', payload: { voice: {...voice, character: value.value}} })
                            }
                            placeholder={"+ " + formatMessage(messages.selectDemo)}
                            options={Object.keys(characterMap).map((key) => ({
                                label: key,
                                value: key,
                              }))
                            }
                          />
                        )}
                      </>
                    )}
                  </>
                }
              </div>
            }
          </div>

          { !!(voice?.languages || []).length &&
            <h3 className="block text-sm text-gray-600 space-x-2">
              <FormattedList
                type="conjunction"
                value={(voice.languages || []).map((language) =>
                  language in languageNames ? formatMessage(languageNames[language]): language
                )}
              />
            </h3>
          }

          <h3 className="block text-sm text-gray-600 space-x-2">
            { genderNames[voice.gender]
              ? <FormattedMessage {...genderNames[voice.gender]} tagName="span" />
              : <span>{voice.gender}</span>
            }

            { navigate &&
              <Link
                to={navigate}
                className="hover:text-primary cursor-pointer border-b border-dotted border-gray-600"
              >
                <FormattedMessage
                  {...messages.showMoreDemos}
                  values={{ count: Math.max(((voice.demos || []).length || 0) - (max || 0), 0) }}
                />
              </Link>
            }
            { roles.includes('coordinator') && voice.turnaround_time &&
              <span title={`${voice.turnaround_time} turnaround time.`} className="uppercase font-medium">{shortTurnaroundTime}</span>
            }
          </h3>

          { !!voice?.unavailability?.length &&
          <Tooltip
            className="p-4 text-sm bg-gray-600 text-white"
            tooltip={
              <div className="max-w-sm space-y-4">
                <h1 className="font-medium"><FormattedMessage {...messages.voicetalentUnavailable}/></h1>
                <ul>
                  { voice.unavailability.map(({ from, to }) => (
                    <li>
                      <FormattedDateTimeRange
                        from={new Date(from)}
                        to={new Date(to)}
                        hour="numeric"
                        minute="numeric"
                      />
                    </li>
                  )) }
                </ul>
              </div>
            }
          >
            <h3
              title={formatMessage(messages.moreDetails)}
              className={classNames('block text-sm space-x-1 font-medium', {
                'text-negative hover:text-negative-lighter': currentUnavailability,
                'text-warning hover:text-warning-lighter': !currentUnavailability,
              })}
            >
              { (currentUnavailability) ? (
                  <span><FormattedMessage {...messages.unavailableWithDate} values={{
                    date: <FormattedDate value={new Date(currentUnavailability.to)}/>
                  }}/></span>
              ) : (
                  <FormattedMessage {...messages.unavailableFallback} values={{
                    span: chunks => <span>{chunks}</span>
                  }}/>
              ) }

              <Icon icon={['fas', 'clock']} />
            </h3>
          </Tooltip>
          }

          <div className="text-xs space-x-1 mt-2 max-h-4 hover:max-h-12 overflow-hidden text-ellipsis whitespace-nowrap hover:whitespace-normal">
            {(voice.tags || []).map((tag) => (
              <span
                key={`${voice.sku}-${tag}`}
                className="text-primary hover:text-primary-darker cursor-pointer"
                onClick={() => dispatch({ type: 'ADD_TAG', payload: tag })}
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {demos.map((demo) => (
        <div
          key={demo.id}
          className="bg-white flex"
          title={product && demos.length == 1 && demos[0].productSlug != product ? <FormattedMessage {...messages.noDemos} values={{
            sku: voice.sku
          }}/> : undefined }
        >
          <Waveform key={demo.url} src={demo.url} />

          <DemoPlayButton
            voice={voice}
            demo={{
              ...demo,
              productSlug: product && demos.length == 1 && demos[0].productSlug != product ? null : demo.productSlug,
            }}
            component="VoiceDemoDos"
            className="min-w-[8rem]"
            childClassName=""
          />

          { (downloadable || onDownload) &&
            <Button
              variant="plain"
              size="sm"
              onClick={() => _onDownload(demo)}
              icon="download"
              className="min-w-max"
            />
          }
        </div>
      ))}

      {(demos.length !== (voice.demos || []).length || showAll) && expandable && (
        <div className="w-full py-px bg-white text-center border-t border-gray-200">
          <button
            className="text-xs font-medium text-gray-400 hover:text-primary leading-none"
            onClick={(event) => {
              event.stopPropagation()
              setShowAll(!showAll)
            }}
          >
            <FormattedMessage
              {...messages[!showAll ? 'expandDemos' : 'collapseDemos']}
              values={{ count: Math.max(((voice.demos || []).length || 0) - (max || 1), 0) }}
            />
          </button>
        </div>
      )}

      {voice.note && (
        <div
          tabIndex={0}
          className="bg-yellow-100 border-t border-yellow-300 text-xs text-gray-700 group p-0 cursor-pointer"
        >
          <div className="p-4 truncate group-focus:hidden h-12">
            {voice.note}
          </div>

          <div className="p-4 hidden group-focus:block">{voice.note}</div>
        </div>
      )}

      {(editable || !!voice.body?.length) && (
        <blockquote
          tabIndex={0}
          className="bg-yellow-100 border-t border-yellow-300 text-gray-700 group p-4 text-sm"
          onBlur={({ currentTarget: { textContent } }) => {
            if (editable && !!textContent?.length) {
              dispatch({
                type: 'PATCH_VOICE',
                payload: { ...voice, body: textContent },
              })
            }
          }}
          onFocus={({ currentTarget }) => {
            if (editable && !voice.body?.length) {
              currentTarget.textContent = ''
            }
          }}
          dangerouslySetInnerHTML={{
            __html:
              voice.body || "<span class='text-gray-400'>" + formatMessage(messages.OptionallyAddNote) + "</span>",
          }}
          contentEditable={editable}
          suppressContentEditableWarning={true}
        />
      )}

      {!editable && onToggle && (
        <div className="flex justify-between items-center p-4">
          {voice.rate && (
            <div>
              <h2 className="text-lg text-primary font-bold">{voice.rate}</h2>
              { voice.rateNote &&
                <small className="text-sm text-gray-600">{ voice.rateNote }</small>
              }
            </div>
          )}

          {selected ? (
            <Button variant="outline" size="sm" onClick={onToggle}>
              <Icon icon="times" /> <FormattedMessage {...messages.deselect} />
            </Button>
          ) : (
            <Button size="sm" onClick={onToggle}>
              <Icon icon="plus" /> <FormattedMessage {...messages.select} />
            </Button>
          )}
        </div>
      )}
    </Card>
  )
}

export default Voice
